import { createTheme } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";
import { ruRU } from "@mui/material/locale";
import { ruRU as dataGridRuRU } from "@mui/x-data-grid/locales";

export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === "dark";

  return createTheme(
  {
    palette: {
      mode,
      primary: { main: isDark ? "#E85068" : "#C8253F", contrastText: "#FFFFFF" },
      secondary: { main: isDark ? "#B0A6B4" : "#5C5158" },
      background: { default: isDark ? "#131017" : "#F5F2F3", paper: isDark ? "#1C1822" : "#FFFFFF" },
      text: {
        primary: isDark ? "#F3EFF3" : "#191319",
        secondary: isDark ? "#B0A6B4" : "#5C5158",
      },
      divider: isDark ? "#312A39" : "#E6DFE2",
      success: { main: isDark ? "#17A280" : "#12876B" },
      warning: { main: isDark ? "#D09321" : "#C77A0A" },
      error: { main: isDark ? "#E85068" : "#C8253F" },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: ['"Golos Text"', '"Segoe UI"', "system-ui", "-apple-system", "sans-serif"].join(","),
      h1: { fontFamily: ['"Onest"', '"Golos Text"', "system-ui", "sans-serif"].join(",") },
      h2: { fontFamily: ['"Onest"', '"Golos Text"', "system-ui", "sans-serif"].join(",") },
      h3: { fontFamily: ['"Onest"', '"Golos Text"', "system-ui", "sans-serif"].join(",") },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? "#131017" : "#F5F2F3",
            color: isDark ? "#F3EFF3" : "#191319",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            color: isDark ? "#F3EFF3" : "#191319",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "var(--rail)",
            color: "var(--rail-text)",
            borderRight: "0",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#221D29" : "#FFFFFF",
            color: isDark ? "#F3EFF3" : "#191319",
            border: `1px solid ${isDark ? "#312A39" : "#E6DFE2"}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? "#F3EFF3" : "#191319",
            "&:hover, &.Mui-focusVisible": {
              backgroundColor: isDark ? "#2A2431" : "#FCEBEE",
              color: isDark ? "#FFFFFF" : "#A31931",
            },
            "&.Mui-selected": {
              backgroundColor: isDark ? "#3A1922" : "#FCEBEE",
              color: isDark ? "#FFFFFF" : "#A31931",
            },
            "&.Mui-selected:hover": {
              backgroundColor: isDark ? "#5B2531" : "#F2C9D1",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: isDark ? "#F3EFF3" : "#191319",
            backgroundColor: isDark ? "#1C1822" : "#FFFFFF",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#463D50" : "#D5CBCF",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#B0A6B4" : "#5C5158",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#E85068" : "#C8253F",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? "#B0A6B4" : "#5C5158",
            "&.Mui-focused": { color: isDark ? "#E85068" : "#C8253F" },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: isDark ? "#B0A6B4" : "#5C5158" },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: isDark ? "#F3EFF3" : "#191319",
            borderBottomColor: isDark ? "#312A39" : "#E6DFE2",
          },
        },
      },
    },
  },
  ruRU,
  dataGridRuRU,
  );
}
